/**
 * Les paliers de maturité — 26/08/2026.
 *
 * ## Pourquoi ce n'est pas une note
 *
 * L'indice d'exposition de ce produit est citable parce qu'il **dérive de
 * l'indice de l'OIT** : la méthode est publiée, la dérivation est écrite, un
 * lecteur peut la contester pièce en main.
 *
 * Aucun référentiel équivalent n'existe pour la maturité. Aucune organisation
 * n'en publie. Un « 2,4 sur 5 » serait donc un chiffre que nous aurions
 * inventé — et il vivrait sur le même écran qu'un indice adossé à une source
 * publique. Le second contaminerait le premier : qui découvre que l'un est
 * inventé cesse de croire l'autre.
 *
 * Ce module ne mesure donc pas. **Il classe.** Chaque palier est défini par des
 * faits que la personne déclare elle-même — un outil partagé, une trace écrite,
 * quelqu'un de responsable. « Vous êtes à l'usage individuel non documenté »
 * ne fait que reformuler ce qui a été répondu ; c'est défendable devant
 * n'importe qui. « Vous êtes à 2,4 » ne l'est pas.
 *
 * ## L'ordre des marches, et pourquoi la relecture vient avant l'écriture
 *
 * Révisé le 26/08/2026, après relecture critique. La première version plaçait
 * « c'est écrit » avant « c'est relu », et se trompait deux fois.
 *
 * D'abord sur les faits : relire ce qu'un collègue a produit est le geste le
 * plus naturel et le plus précoce ; écrire une procédure est un effort
 * d'organisation, donc plus tardif. Une entreprise qui relit systématiquement
 * sans rien avoir écrit se voyait classée « usage partagé », et sa seule
 * pratique protectrice n'apparaissait nulle part dans son palier.
 *
 * Ensuite sur les priorités : **un document erroné qui part chez un client
 * coûte plus cher qu'une pratique non documentée.** Placer l'écriture avant la
 * relecture faisait passer le rangement avant le risque, et la trajectoire
 * conseillait de ranger à qui aurait dû d'abord se protéger.
 *
 * ## Le classement produit la trajectoire, et c'est le point
 *
 * Les conditions sont **ordonnées et cumulatives** : un palier est atteint
 * quand toutes celles qui le précèdent le sont. La première condition non
 * remplie est donc, littéralement, la marche suivante — et la liste de celles
 * qui restent est la trajectoire.
 *
 * Elle n'est pas rédigée par le modèle. Règle n° 2 du projet : le score est
 * calculé par le code. Une trajectoire l'est aussi, sans quoi deux dirigeants
 * dans la même situation recevraient deux chemins différents, et le document
 * cesserait d'être opposable.
 *
 * ## Pas de calendrier inventé
 *
 * Le porteur voulait trois à dix-huit mois. Nous ne savons rien du rythme de
 * cette entreprise-là, et une échéance devinée est une échéance qu'on manque —
 * c'est-à-dire un faux signal, règle n° 6, sur un document payé.
 *
 * Chaque condition porte à la place `sansAttendre` : peut-elle être remplie
 * par la seule décision de la personne qui lit, ou suppose-t-elle l'accord ou
 * le temps de quelqu'un d'autre ? Le seul horizon daté que ce produit
 * s'autorise est celui qui existe déjà : le rendez-vous à quatre-vingt-dix
 * jours. Ce qui est `sansAttendre` devrait avoir bougé d'ici là ; le reste,
 * non, et le dire est plus utile que promettre un trimestre.
 */
/**
 * L'échelle du dirigeant : elle parle d'équipe.
 *
 * La question qui la structure n'est pas « utilisez-vous l'IA » — presque tout
 * le monde répond oui, et la réponse ne décide de rien. C'est **qu'est-ce qui
 * survit au départ de la personne la plus à l'aise**. Un usage qui ne survit
 * pas n'est pas un actif de l'entreprise, c'est une astuce personnelle.
 */
const DIRIGEANT = {
    paliers: [
        {
            rang: 0,
            nom: "Aucun usage",
            dit: "Personne ne se sert d'une IA pour le travail.",
            coute: "Chaque heure listée plus haut est encore faite entièrement à la main.",
        },
        {
            rang: 1,
            nom: "Usage individuel",
            dit: "Une personne s'en sert, à sa façon.",
            coute: "Ce qu'elle gagne, elle seule le gagne. L'entreprise, elle, ne gagne rien qui lui reste.",
        },
        {
            rang: 2,
            nom: "Usage partagé",
            dit: "Plusieurs personnes s'en servent.",
            coute: "Chacune refait ses propres essais. Le même problème est résolu plusieurs fois, et mal résolu autant de fois.",
        },
        {
            rang: 3,
            nom: "Usage relu",
            dit: "Ce que produit l'IA passe sous les yeux d'un humain avant de sortir.",
            coute: "Chacun relit selon son propre jugement, et rien n'est écrit : ce jugement part avec la personne qui l'exerce.",
        },
        {
            rang: 4,
            nom: "Usage écrit",
            dit: "Relu, et la façon de faire est écrite et retrouvable.",
            coute: "Tout repose encore sur la personne la plus à l'aise. Son départ emporte ce que l'entreprise croyait acquis.",
        },
        {
            rang: 5,
            nom: "Usage tenu",
            dit: "Écrit, contrôlé, et qui survit à une absence.",
            coute: "",
        },
    ],
    conditions: [
        {
            cle: "utilise",
            question: "Quelqu'un dans l'entreprise se sert-il d'une IA pour son travail, au moins une fois par semaine ?",
            acquis: "Au moins une personne s'en sert chaque semaine.",
            marche: "Faites essayer une seule tâche, par une seule personne, pendant une semaine. Il n'y a pas d'outil à choisir : il y a une tâche à confier.",
            sansAttendre: true,
            semainesTypiques: 2,
        },
        {
            cle: "plusieurs",
            question: "Sont-ils plus d'un à s'en servir ?",
            acquis: "Plusieurs personnes s'en servent.",
            marche: "Demandez à celle qui s'en sert de montrer, une fois, à une seule autre. Une démonstration de vingt minutes, pas une formation à organiser.",
            sansAttendre: true,
            semainesTypiques: 4,
        },
        {
            cle: "verifie",
            question: "Ce que produit l'IA est-il relu par un humain avant d'être utilisé ou envoyé ?",
            acquis: "Rien ne sort sans relecture.",
            marche: "Posez la règle : rien ne part chez un client ni dans un compte sans relecture. Elle ne coûte rien tant qu'aucune erreur n'est passée, et tout le jour où l'une passe.",
            sansAttendre: true,
            semainesTypiques: 4,
        },
        {
            cle: "ecrit",
            question: "La façon de faire est-elle écrite quelque part — une note, un document, un message épinglé ?",
            acquis: "La façon de faire est écrite et retrouvable.",
            marche: "Faites écrire dix lignes par celle qui s'en sert le mieux : ce qu'elle demande au modèle, et ce qu'elle refuse d'accepter en retour. La seconde moitié est celle qui manque toujours.",
            sansAttendre: true,
            semainesTypiques: 6,
        },
        {
            cle: "survit",
            question: "Si la personne la plus à l'aise part trois semaines, le travail continue-t-il de la même façon ?",
            acquis: "L'usage survit à une absence.",
            marche: "Éprouvez-le pendant ses congés : ce qui casse à ce moment-là est exactement ce qui n'était écrit nulle part. C'est la seule vérification qui ne se triche pas, et elle demande d'attendre l'occasion.",
            sansAttendre: false,
            semainesTypiques: 26,
        },
    ],
};
/**
 * L'échelle de l'indépendant : elle parle de ce qui tourne sans lui.
 *
 * L'échelle du dirigeant ne s'y applique pas — « sont-ils plusieurs » n'a pas
 * de sens quand on est seul. La question qui structure celle-ci est **ce qui
 * serait encore là si vous ne le refaisiez pas vous-même**, parce que c'est la
 * seule chose qui distingue un métier d'un revenu porté à bout de bras.
 */
const INDEPENDANT = {
    paliers: [
        {
            rang: 0,
            nom: "Aucun usage",
            dit: "Vous ne vous servez pas d'une IA pour votre travail.",
            coute: "Chaque heure listée plus haut est encore faite entièrement à la main.",
        },
        {
            rang: 1,
            nom: "Au coup par coup",
            dit: "Vous vous en servez quand vous y pensez.",
            coute: "Vous repartez de zéro à chaque fois, y compris quand c'est la tâche d'hier.",
        },
        {
            rang: 2,
            nom: "Habitude",
            dit: "Vous vous en servez chaque semaine, sur les mêmes tâches.",
            coute: "Ce qui marche n'est gardé nulle part. Le temps gagné se regagne à chaque fois, au lieu de s'accumuler.",
        },
        {
            rang: 3,
            nom: "Contrôlé",
            dit: "Rien ne part chez un client sans que vous l'ayez vérifié.",
            coute: "Ce qui marche n'est toujours conservé nulle part : vous refaites la mise au point à chaque fois.",
        },
        {
            rang: 4,
            nom: "Reproductible",
            dit: "Vérifié, et ce qui marche est conservé et réutilisé.",
            coute: "Tout tient dans votre tête et sur votre écran : vous ne pouvez ni déléguer, ni vous absenter, ni vendre ce savoir-faire.",
        },
        {
            rang: 5,
            nom: "Transmissible",
            dit: "Exécutable par quelqu'un d'autre que vous, en suivant vos notes.",
            coute: "",
        },
    ],
    conditions: [
        {
            cle: "utilise",
            question: "Vous servez-vous d'une IA pour votre travail, au moins une fois par semaine ?",
            acquis: "Vous vous en servez chaque semaine.",
            marche: "Prenez la tâche que vous détestez le plus dans la liste plus haut, et confiez-la une fois. Une seule tâche, une seule semaine.",
            sansAttendre: true,
            semainesTypiques: 2,
        },
        {
            cle: "regulier",
            question: "Est-ce sur les mêmes tâches, qui reviennent chaque semaine ?",
            acquis: "L'usage porte sur des tâches récurrentes.",
            marche: "Choisissez deux tâches qui reviennent toutes les semaines. Le gain d'une tâche exceptionnelle ne se répète jamais, quel que soit le temps qu'il vous a fait gagner ce jour-là.",
            sansAttendre: true,
            semainesTypiques: 3,
        },
        {
            cle: "verifie",
            question: "Vérifiez-vous systématiquement le résultat avant qu'il parte chez un client ?",
            acquis: "Rien ne part chez un client sans vérification.",
            marche: "Posez-vous la règle par écrit, à côté de vos gabarits. Un seul document erroné arrivé chez un client coûte plus que toutes les heures gagnées avant lui.",
            sansAttendre: true,
            semainesTypiques: 3,
        },
        {
            cle: "garde",
            question: "Gardez-vous ce qui a bien marché — la demande exacte, un gabarit — pour le réutiliser ?",
            acquis: "Ce qui marche est conservé et réutilisé.",
            marche: "Ouvrez un seul fichier. Chaque fois qu'une demande donne un bon résultat, collez-la dedans avec deux mots de contexte. C'est tout, et c'est ce qui change le plus vite votre semaine.",
            sansAttendre: true,
            semainesTypiques: 4,
        },
        {
            cle: "transmissible",
            question: "Quelqu'un d'autre pourrait-il exécuter ce travail en suivant vos notes, sans vous demander ?",
            acquis: "Le travail est exécutable par quelqu'un d'autre.",
            marche: "Faites l'essai avec une personne réelle, sur une seule tâche. Ce qu'elle vient vous demander est exactement ce qui manque à vos notes — et il faut quelqu'un d'autre pour le découvrir.",
            sansAttendre: false,
            semainesTypiques: 12,
        },
    ],
};
export function echelle(statut) {
    return statut === "independant" ? INDEPENDANT : DIRIGEANT;
}
/**
 * Classe, et ne note pas.
 *
 * Le classement s'arrête à la **première** condition non remplie, et n'examine
 * pas les suivantes pour créditer un palier plus haut. C'est délibéré : une
 * entreprise qui vérifie ses résultats mais n'a rien écrit n'est pas plus
 * avancée que celle qui n'a rien écrit, elle est au même endroit avec une
 * bonne habitude. Créditer le saut donnerait un palier flatteur et une
 * trajectoire fausse — or c'est la trajectoire qui est vendue.
 */
export function classer(statut, reponses) {
    const { paliers, conditions } = echelle(statut);
    /*
      `undefined` est accepté, et vaut « rien répondu ».
  
      Ce n'est pas de la complaisance : les transactions payées avant le 26/08
      ne portent pas ce champ, et l'une d'elles peut être relancée demain. Une
      fonction qui lèverait ici ferait échouer la rédaction d'un rapport déjà
      payé — c'est-à-dire prendre l'argent sans rien remettre, sur la marche que
      ce dépôt vient précisément d'instrumenter.
    */
    const dites = reponses ?? {};
    const renseigne = conditions.some((c) => c.cle in dites);
    let rang = 0;
    while (rang < conditions.length && dites[conditions[rang].cle] === true)
        rang += 1;
    return {
        statut,
        palier: paliers[rang],
        acquis: conditions.slice(0, rang),
        restantes: conditions.slice(rang),
        renseigne,
    };
}
/**
 * Ce qui devrait avoir bougé d'ici le rendez-vous à quatre-vingt-dix jours.
 *
 * Le seul horizon daté que ce produit s'autorise, parce que c'est le seul qui
 * existe déjà : l'acheteur est rappelé à quatre-vingt-dix jours et refait son
 * diagnostic. Ne sont retenues que les marches qui ne dépendent de personne
 * d'autre — les autres sont réelles mais leur délai serait deviné.
 */
export function dIciQuatreVingtDixJours(classement) {
    return classement.restantes.filter((c) => c.sansAttendre);
}
/**
 * La trajectoire, avec les délais que le porteur a demandés — 26/08/2026.
 *
 * ## Ce que ce calcul est, et ce qu'il n'est pas
 *
 * C'est de l'arithmétique sur des durées déclarées dans `maturite.ts` : la
 * somme des semaines typiques, ramenée en mois, arrondie au mois supérieur —
 * jamais à l'inférieur. Arrondir vers le bas rendrait chaque échéance un peu
 * plus proche qu'elle ne l'est, et une trajectoire entière un peu plus courte
 * que la réalité : la même complaisance qu'un arrondi de comptable, appliquée
 * à une promesse.
 *
 * Ce n'est **pas** une prévision sur cette entreprise-là. Nous ne connaissons
 * ni sa charge, ni ses priorités, ni qui décide chez elle. Le porteur a tranché
 * le 26/08 en connaissance de cause, contre mon avis, et l'arbitrage tient à
 * une condition tenue par un test : le document dit qu'il s'agit d'une
 * estimation, à l'endroit où il donne les dates.
 *
 * Le cumul part de la position **actuelle**, pas de zéro : quelqu'un au palier
 * trois s'entend dire ce que coûtent les marches qui lui restent, et non celles
 * qu'il a déjà franchies.
 */
export function trajectoireDatee(classement) {
    let semaines = 0;
    return classement.restantes.map((condition, index) => {
        semaines += condition.semainesTypiques;
        return {
            condition,
            numero: index + 1,
            // Vers le haut, toujours : une échéance arrondie au plus court est une
            // échéance qu'on manque de peu, et c'est celle-là qu'on retient.
            moisTypique: Math.max(1, Math.ceil(semaines / 4.34)),
        };
    });
}
/**
 * La phrase qui accompagne obligatoirement les dates.
 *
 * Elle vit ici et non dans le gabarit du PDF : un test vérifie que le document
 * la porte, et une phrase recopiée à la main dans un fichier de mise en page
 * finit par diverger de celle qu'on a éprouvée.
 */
export const AVERTISSEMENT_DELAIS = "Ces délais sont une estimation de ce que ces gestes demandent en général, et non une prévision sur votre entreprise : nous ne connaissons ni votre charge, ni vos priorités, ni qui décide chez vous. Lisez-les comme un ordre de grandeur, jamais comme un engagement. Le seul rendez-vous ferme est celui des quatre-vingt-dix jours.";
/**
 * L'imprudence la plus fréquente, et la moins nommée.
 *
 * Le texte ne parle ni d'amende ni de sanction : nous ne sommes pas en mesure
 * de qualifier une situation juridique, et le faire serait exactement le faux
 * signal que la règle n° 6 interdit — appliqué cette fois à la peur. Il dit ce
 * qui est vrai et vérifiable : ce qui est saisi dans un outil grand public
 * sort de l'entreprise, et l'entreprise ne sait plus où il est.
 */
export const QUESTION_DONNEES = {
    cle: "donneesClients",
    question: "Des informations de vos clients — noms, montants, contrats — ont-elles déjà été saisies dans un outil d'IA grand public ?",
    siOui: "Ces informations sont sorties de votre entreprise, et vous ne savez plus où elles sont ni combien de temps elles y restent. Ce n'est pas irréparable : la plupart des outils proposent un réglage qui empêche la réutilisation des saisies, et les offres professionnelles le posent par défaut. La première chose à faire est de savoir qui a saisi quoi.",
};
/** Les questions hors échelle, dans l'ordre où elles se posent. */
export const HORS_ECHELLE = [QUESTION_DONNEES];
/**
 * La réserve du dernier palier — 26/08/2026.
 *
 * ## Le faux signal qu'elle répare
 *
 * Le dernier palier portait `coute: ""` : rien à reprocher. C'était faux, et
 * je l'avais écrit moi-même.
 *
 * Une entreprise peut atteindre « usage tenu » — relu, écrit, survivant à une
 * absence — **sur la rédaction d'e-mails**, pendant que les sept postes de son
 * tableau n'ont rien. Elle lisait « ce palier ne coûte rien » et avait tort de
 * le croire.
 *
 * L'échelle mesure l'**institutionnalisation**, pas le **levier**. Les deux
 * sont indépendants : on peut être parfaitement organisé sur une tâche
 * marginale. Le dernier palier ne peut donc rien affirmer sans regarder ce que
 * l'usage couvre réellement — et cette information existe, depuis la question
 * hors échelle sur les postes.
 *
 * @returns La réserve à afficher, ou `null` quand il n'y en a pas — soit que
 *   tout soit couvert, soit qu'on ne sache pas ce qui l'est, auquel cas on ne
 *   reproche rien plutôt que de reprocher au hasard.
 */
export function reserveDuDernierPalier(postesCouverts, postesDeclares) {
    // Sans postes déclarés, ou sans réponse sur leur couverture, on ne sait pas :
    // et ne pas savoir ne se dit pas comme un reproche.
    if (postesDeclares <= 0 || postesCouverts <= 0)
        return null;
    const restants = postesDeclares - postesCouverts;
    if (restants <= 0)
        return null;
    return `Ce palier décrit la façon dont vous vous organisez, pas l'étendue de ce que vous avez confié à une IA. ${restants === 1 ? "Un poste de votre tableau" : `${restants} postes de votre tableau`} n'${restants === 1 ? "est" : "sont"} toujours pas concerné${restants === 1 ? "" : "s"} : c'est là que se trouve tout ce qui vous reste à gagner, et aucune organisation ne le gagnera à votre place.`;
}
